/**
 * Mock data for the Operative Dashboard — super-admin-only view that
 * monitors ADMINS: what they've done, when, and how much.
 *
 * Shape mirrors what the real `/admin/operative/activity` endpoint
 * should return — 8 weeks of history per admin, with per-day
 * granularity for the current week. Same idiom as the TDB Portal
 * Activity dashboard: an admin operator scans a compact strip to see
 * who is engaged and who has gone quiet, then expands a row for the
 * week-by-week history.
 *
 * Backend origin (when real):
 *   - Sourced from API Gateway access logs joined with auth-session records
 *   - "Active time" counts half-hour blocks where the account did anything
 *     beyond the app's automatic background polling (an idle open tab
 *     scores zero)
 *   - "Actions" are categorised by request type into:
 *       contacts, status_changes, comments, uploads, admin_management
 *   - Aggregated weekly Sun–Sat in UTC
 */

export type ActionCategory =
  | 'contacts'         // outbound calls, texts, emails logged
  | 'status_changes'   // JV / DFPB stage transitions
  | 'comments'         // admin comments on submissions
  | 'uploads'          // MAO reports, photos, attachments
  | 'admin_mgmt';      // user/permission management

export type AdminRole = 'super_admin' | 'admin_manager' | 'admin';

export interface AdminDayActivity {
  /** Raw request count for the day — feeds the M-T-W-T-F strip quantile. */
  requests: number;
  /** Half-hour blocks the admin was actively working. */
  activeHours: number;
  /** Per-category action breakdown for the day. */
  actions: Partial<Record<ActionCategory, number>>;
}

export interface AdminWeek {
  /** Week start (Sunday) ISO date. */
  weekStart: string;
  /** Total request count over the week. */
  requests: number;
  /** Active hours summed across the week. */
  activeHours: number;
  /** Total online hours (open tab, including idle). */
  onlineHours: number;
  /** Total write actions across all categories. */
  totalActions: number;
  /** Per-category totals. */
  categories: Partial<Record<ActionCategory, number>>;
  /** Day-of-week (0=Sun..6=Sat) to activity. Missing = no activity. */
  days: Record<number, AdminDayActivity>;
}

export interface AdminRow {
  /** Login email — real primary key today. */
  email: string;
  /** Short display name. */
  displayName: string;
  role: AdminRole;
  /** Weeks in reverse-chronological order; length 8. */
  weeks: AdminWeek[];
  /** Convenience: this week's active hours + total actions. */
  thisWeekActiveHours: number;
  thisWeekActions: number;
  /** SLA compliance: fraction of new leads this admin owned that were
   *  contacted within 24h. 0-1. Null when the admin had no owned leads. */
  slaCompliance: number | null;
  /** Number of active deals this admin currently owns. */
  ownedDeals: number;
  /** Deals closed attributed to this admin (all-time). */
  closedAttributed: number;
}

export interface SlaBreachedEntry {
  submissionId: string;
  address: string;
  assignedTo: string | null;
  status: string;
  /** "Partner message unanswered" | "New lead unacknowledged" */
  breachReason: string;
  /** Hours past the SLA threshold, sorted descending by backend. */
  hoursOverdue: number;
  createdAt: string | null;
  lastCommentAt: string | null;
  /** Stamped on admin text comments and emoji reactions. Max with lastCommentAt = true last touch. */
  lastAdminAckAt: string | null;
  /** 'real' = partner waiting on genuine answer or lead untouched; 'fyi' = routine note */
  breachTier: 'real' | 'fyi';
  partnerName: string | null;
  /** Last comment text, truncated to 200 chars by backend. */
  partnerLastMessage: string | null;
}

export interface OperativePayload {
  generatedAt: string;
  /** Sun–Sat of the current display week (ISO dates). */
  currentWeek: { start: string; end: string };
  totals: {
    activeAdmins: number;
    totalActionsThisWeek: number;
    totalActiveHoursThisWeek: number;
    /** Fleet-wide SLA compliance this week. */
    slaComplianceThisWeek: number;
    /** How many contact-SLA breaches this week (24h without contact). */
    slaBreachesThisWeek: number;
  };
  admins: AdminRow[];
  /** Fleet-wide per-week actions by category (last 8 weeks). Ordered
   *  oldest→newest for a left-to-right chart. */
  fleetActionsByWeek: {
    weekStart: string;
    byCategory: Partial<Record<ActionCategory, number>>;
    total: number;
  }[];
  /** Active submissions that have exceeded the 24h acknowledgment SLA.
   *  Sorted worst-first (highest hoursOverdue first). */
  slaBreached?: SlaBreachedEntry[];
}

// ---------------------------------------------------------------------------
// Category display metadata — kept next to the type so callers don't
// hard-code labels or colors.
// ---------------------------------------------------------------------------

export const CATEGORY_LABEL: Record<ActionCategory, string> = {
  contacts:       'Contacts',
  status_changes: 'Status changes',
  comments:       'Comments & reactions',
  uploads:        'Uploads',
  admin_mgmt:     'Admin mgmt',
};

export const CATEGORY_COLOR: Record<ActionCategory, string> = {
  contacts:       '#2a78d6',
  status_changes: '#eb6834',
  comments:       '#1baf7a',
  uploads:        '#eda100',
  admin_mgmt:     '#008300',
};

// contacts excluded: not tracked by backend (placeholder for future CRM logging)
export const CATEGORY_ORDER: ActionCategory[] = [
  'status_changes', 'comments', 'uploads', 'admin_mgmt',
];

/** Quantile bucket 0-4 for a day's request count against a week's peak. */
export function dayQuantile(dayRequests: number, weekPeak: number): 0 | 1 | 2 | 3 | 4 {
  if (dayRequests <= 0 || weekPeak <= 0) return 0;
  const frac = dayRequests / weekPeak;
  if (frac >= 0.85) return 4;
  if (frac >= 0.60) return 3;
  if (frac >= 0.35) return 2;
  if (frac >  0)    return 1;
  return 0;
}

export function formatWeekLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
